import missingFacilityImage from "assets/images/missing-facility.jpg";
import { formatOpeningHours } from "utils/formatOpeningHours";

const paymentMethodsMap = {
	cashAccepted: "Cash",
	creditCardsAccepted: "Credit Card",
	checksAccepted: "Check"
};

const buildPaymentAccepted = (billingAmenities) => {
	if (!billingAmenities) {
		return undefined;
	}

	// Iterate paymentMethodsMap's key order (not billingAmenities'), so the
	// output order is deterministic regardless of how the gateway serializes it.
	const paymentMethods = Object.keys(paymentMethodsMap).filter(
		(key) => billingAmenities[key]?.hasAttribute === true
	);

	return paymentMethods.length
		? paymentMethods.map((key) => paymentMethodsMap[key])
		: undefined;
};

export const getFacilityCardProps = (facility) => {
	if (Object.keys(facility).length === 0) return null;

	const {
		name,
		images,
		location,
		address,
		facilityPhone,
		distance,
		url,
		lowestPrice,
		startingPrice,
		sortId,
		id,
		reviews,
		unitPromotion,
		recommendedUnit,
		closures,
		adminFee,
		lockFee,
		totalFees,
		showFeeWarning,
		staticMapUrl,
		hasOnlineMoveins,
		promotions,
		hours,
		billingAmenities,
		amenities: facilityAmenities
	} = facility;

	const MAX_AMENITIES_TO_SHOW = 5;

	// images
	const IMAGE_BASE_PATH = "https://images.storage.com/medium-compress/";
	const facilityImage = images.length
		? `${IMAGE_BASE_PATH}${images[0]}`
		: missingFacilityImage;

	// format the object we'll turn into our SEO schema
	const schema = {
		name,
		location,
		reviews,
		facilityPhone,
		promotion: unitPromotion,
		// don't send the default to schema, it'll be base64
		images: images.length ? [facilityImage] : [],
		url,
		closures,
		staticMapUrl,
		hasOnlineMoveins,
		openingHoursSpecification: formatOpeningHours(hours),
		amenities: facilityAmenities || {},
		paymentAccepted: buildPaymentAccepted(billingAmenities)
	};

	return {
		name,
		sortId,
		id,
		showDefaultImage: !images.length,
		image: facilityImage,
		address,
		distance,
		url,
		startingPrice,
		lowestPrice,
		reviews,
		facilityPhone,
		hasOnlineMoveins,
		// Mirrors ssdr's getDealText: after the facility-level specialString and
		// the sized-unit promo (unitPromotion, from findUnitPromotions), fall back
		// to the recommended unit's special — findUnitPromotions deliberately omits
		// it, so a promo living only on that unit would otherwise show no banner.
		dealBannerText:
			promotions?.specialString ||
			unitPromotion ||
			recommendedUnit?.special ||
			null,
		amenities:
			recommendedUnit.availableAmenities?.slice(
				0,
				MAX_AMENITIES_TO_SHOW
			) || [],
		hasAdditionalAmenities:
			recommendedUnit.availableAmenities?.length > MAX_AMENITIES_TO_SHOW,
		schema,
		closures,
		adminFee,
		lockFee,
		totalFees,
		showFeeWarning
	};
};

export const getFacilityWithUnits = (id, facilitiesById, unitsById) => {
	const facility = facilitiesById[id] || {};
	const facilityObject = { ...facility };
	if (Object.keys(facility).length > 0) {
		const recommendedUnitId = facilitiesById[id].units?.recommendedUnit;
		facilityObject.recommendedUnit = unitsById[recommendedUnitId] || {};
	}
	return facilityObject;
};

export default getFacilityWithUnits;
